import React, { useState, useEffect } from "react";
import { apiFetch } from "../../../../core/utils/api";
import { Toggle } from "../../../../design-system/components/inputs";
import { PrimaryButton } from "../../../../design-system";
import { typography, surfaces, borders, textColors, interactive } from "../../../../design-system/tokens";

const NotificationsSettings = ({ username }) => {
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    pushNotifications: false,
    marketingEmails: true,
    securityAlerts: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Load notification settings on component mount
  useEffect(() => {
    const loadNotificationSettings = async () => {
      try {
        const response = await apiFetch("/api/auth/notifications/", {
          method: "GET",
          credentials: "include",
        });

        if (response.ok) {
          const data = await response.json();
          setNotificationSettings(data);
        } else {
          console.error("Failed to load notification settings");
        }
      } catch (err) {
        console.error("Error loading notification settings:", err);
      } finally {
        setLoading(false);
      }
    };

    loadNotificationSettings();
  }, []);

  const handleNotificationChange = (key, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    try {
      const response = await apiFetch("/api/auth/notifications/update/", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json"
        },
        credentials: "include",
        body: JSON.stringify(notificationSettings)
      });

      if (response.ok) {
        const data = await response.json();
        setNotificationSettings(data.notifications);
      } else {
        let errorMessage = "Failed to save notification settings. Please try again.";
        try {
          const errorData = await response.json();
          errorMessage = errorData?.error || errorMessage;
        } catch (e) {
          // Ignore JSON parse errors; keep fallback message.
        }
        setSaveError(errorMessage);
      }
    } catch (err) {
      console.error("Error saving notification settings:", err);
      setSaveError("Network error. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className={textColors.secondary}>Loading notification settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Email Notifications */}
      <div className={`rounded-2xl shadow-sm ${surfaces.glassCard} border ${borders.glass}`}>
        <div className={`px-6 py-4 border-b ${borders.glass}`}>
          <h2 className={`${typography.h5} ${textColors.primary}`}>Email Notifications</h2>
          <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>Choose how you want to be notified via email.</p>
        </div>
        
        <div className="p-6">
          <div className="space-y-4">
            <Toggle
              checked={notificationSettings.emailNotifications}
              onChange={(e) => handleNotificationChange("emailNotifications", e.target.checked)}
              label="General Email Notifications"
              description="Receive updates and notifications via email"
              className={`w-full p-4 rounded-xl border ${borders.glass} ${surfaces.glass} ${surfaces.hover} transition-colors`}
            />

            <Toggle
              checked={notificationSettings.marketingEmails}
              onChange={(e) => handleNotificationChange("marketingEmails", e.target.checked)}
              label="Marketing Emails"
              description="Receive promotional content and updates"
              className={`w-full p-4 rounded-xl border ${borders.glass} ${surfaces.glass} ${surfaces.hover} transition-colors`}
            />

            <Toggle
              checked={notificationSettings.securityAlerts}
              onChange={(e) => handleNotificationChange("securityAlerts", e.target.checked)}
              label="Security Alerts"
              description="Important security notifications (recommended)"
              className={`w-full p-4 rounded-xl border ${borders.glass} ${surfaces.glass} ${surfaces.hover} transition-colors`}
            />
          </div>
        </div>
      </div>

      {/* Push Notifications */}
      <div className={`rounded-2xl shadow-sm ${surfaces.glassCard} border ${borders.glass}`}>
        <div className={`px-6 py-4 border-b ${borders.glass}`}>
          <h2 className={`${typography.h5} ${textColors.primary}`}>Push Notifications</h2>
          <p className={`${typography.bodySmall} ${textColors.secondary} mt-1`}>Browser notifications for real-time updates.</p>
        </div>
        
        <div className="p-6">
          <Toggle
            checked={notificationSettings.pushNotifications}
            onChange={(e) => handleNotificationChange("pushNotifications", e.target.checked)}
            label="Browser Push Notifications"
            description="Receive notifications in your browser"
            className={`w-full p-4 rounded-xl border ${borders.glass} ${surfaces.glass} ${surfaces.hover} transition-colors`}
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex flex-col items-end gap-2">
        {saveError ? (
          <div className={`text-sm ${textColors.danger}`}>{saveError}</div>
        ) : null}
        <PrimaryButton onClick={handleSave} loading={saving} className={interactive.primaryGradientBtn}>
          Save Notification Settings
        </PrimaryButton>
      </div>
    </div>
  );
};

export default NotificationsSettings; 